import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import color from "../../../../Constants/colors";
import {
  CURRENCIES,
  Currency,
  DealData,
  DealField,
  ServiceArea,
} from "../../../../Models/ambassador";
import { formatQuarter, upcomingQuarters } from "../../Common/format";
import {
  CONTACT_AGREED,
  CONTRIBUTIONS,
  COUNTRIES,
  CUSTOMER_RELATIONSHIPS,
  DEAL_READINESS,
  OPPORTUNITY_TYPES,
  SERVICE_AREAS,
  useAmbassadorLabels,
} from "../../Common/useAmbassadorLabels";
import { DealErrors } from "./dealFormModel";

export interface StepProps {
  data: DealData;
  errors: DealErrors;
  onChange: (patch: Partial<DealData>) => void;
}

const REQUIRED_FIELDS: DealField[] = [
  "company_name",
  "customer_relationship",
  "title",
  "opportunity_type",
  "description",
  "service_areas",
];

export const FormGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: "repeat(2, minmax(0, 1fr))",
      },
      gap: 2,
    }}
  >
    {children}
  </Box>
);

export const FormSection: React.FC<{ title: string; hint?: string }> = ({
  title,
  hint,
}) => (
  <Box
    sx={{
      gridColumn: "1 / -1",
      pt: 1,
      mt: 1,
      borderTop: `1px solid ${color.gray100}`,
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
      {title}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: color.gray500 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/** Text field bound to one deal field, with its label and error. */
const useField = ({ data, errors, onChange }: StepProps) => {
  const { t } = useTranslation("ambassador");
  return (field: DealField, props: TextFieldProps = {}): TextFieldProps => ({
    label: t(`fields.${field}`),
    required: REQUIRED_FIELDS.includes(field),
    value: (data[field] as string | number | null) ?? "",
    onChange: (event) => onChange({ [field]: event.target.value }),
    error: Boolean(errors[field]),
    helperText: errors[field] ? t(errors[field] as string) : props.helperText,
    fullWidth: true,
    size: "small",
    ...props,
  });
};

/** Select of an enum field; optional fields can be cleared again. */
const EnumSelect = <T extends number>({
  field,
  values,
  label,
  stepProps,
}: {
  field: DealField;
  values: T[];
  label: (value: T) => string;
  stepProps: StepProps;
}) => {
  const { t } = useTranslation("ambassador");
  const fieldProps = useField(stepProps);
  const required = REQUIRED_FIELDS.includes(field);
  return (
    <TextField
      {...fieldProps(field, {
        select: true,
        onChange: (event) =>
          stepProps.onChange({
            [field]:
              event.target.value === "" ? null : Number(event.target.value),
          }),
      })}
    >
      {!required && <MenuItem value="">{t("wizard.notSpecified")}</MenuItem>}
      {values.map((value) => (
        <MenuItem key={value} value={value}>
          {label(value)}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const CustomerStep: React.FC<StepProps> = (props) => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const field = useField(props);

  return (
    <FormGrid>
      <TextField
        {...field("company_name", { inputProps: { maxLength: 200 } })}
      />
      <EnumSelect
        field="customer_relationship"
        values={CUSTOMER_RELATIONSHIPS}
        label={labels.customerRelationship}
        stepProps={props}
      />
      <TextField
        {...field("company_website", {
          placeholder: "www.example.com",
          inputProps: { maxLength: 300 },
        })}
      />
      <TextField {...field("company_country", { select: true })}>
        <MenuItem value="">{t("wizard.notSpecified")}</MenuItem>
        {COUNTRIES.map((code) => (
          <MenuItem key={code} value={code}>
            {labels.country(code)}
          </MenuItem>
        ))}
      </TextField>

      <FormSection
        title={t("wizard.contactSection")}
        hint={t("wizard.contactHint")}
      />
      <TextField
        {...field("contact_name", {
          placeholder: t("wizard.contactNamePlaceholder"),
          inputProps: { maxLength: 200 },
        })}
      />
      <TextField
        {...field("contact_role", {
          placeholder: t("wizard.contactRolePlaceholder"),
          inputProps: { maxLength: 200 },
        })}
      />
      <TextField
        {...field("contact_email", {
          type: "email",
          placeholder: t("wizard.contactEmailPlaceholder"),
          inputProps: { maxLength: 254 },
        })}
      />
      <EnumSelect
        field="contact_agreed"
        values={CONTACT_AGREED}
        label={labels.contactAgreed}
        stepProps={props}
      />
    </FormGrid>
  );
};

export const ProjectStep: React.FC<StepProps> = (props) => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const field = useField(props);
  const { data, errors, onChange } = props;

  const quarters = upcomingQuarters();
  if (data.expected_start && !quarters.includes(data.expected_start)) {
    quarters.unshift(data.expected_start);
  }

  const toggleArea = (area: ServiceArea) =>
    onChange({
      service_areas: data.service_areas.includes(area)
        ? data.service_areas.filter((value) => value !== area)
        : [...data.service_areas, area],
    });

  return (
    <FormGrid>
      <TextField {...field("title", { inputProps: { maxLength: 200 } })} />
      <EnumSelect
        field="opportunity_type"
        values={OPPORTUNITY_TYPES}
        label={labels.opportunityType}
        stepProps={props}
      />
      <Box sx={{ gridColumn: "1 / -1" }}>
        <TextField
          {...field("description", {
            multiline: true,
            minRows: 3,
            placeholder: t("wizard.descriptionPlaceholder"),
            inputProps: { maxLength: 5000 },
          })}
        />
      </Box>

      <FormSection
        title={t("wizard.servicesSection")}
        hint={t("wizard.servicesHint")}
      />
      <Box
        sx={{
          gridColumn: "1 / -1",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1,
        }}
      >
        {SERVICE_AREAS.map((area) => {
          const checked = data.service_areas.includes(area);
          return (
            <FormControlLabel
              key={area}
              control={
                <Checkbox checked={checked} onChange={() => toggleArea(area)} />
              }
              label={labels.serviceArea(area)}
              sx={{
                m: 0,
                pr: 1,
                borderRadius: "9px",
                border: `1px solid ${checked ? color.gray400 : color.gray200}`,
                "& .MuiFormControlLabel-label": { fontSize: 14 },
              }}
            />
          );
        })}
        {errors.service_areas && (
          <FormHelperText error sx={{ gridColumn: "1 / -1" }}>
            {t("wizard.errors.serviceAreas")}
          </FormHelperText>
        )}
      </Box>

      <FormSection
        title={t("wizard.timingSection")}
        hint={t("wizard.timingHint")}
      />
      <TextField {...field("expected_start", { select: true })}>
        <MenuItem value="">{t("wizard.notSpecified")}</MenuItem>
        {quarters.map((quarter) => (
          <MenuItem key={quarter} value={quarter}>
            {formatQuarter(quarter)}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          {...field("expected_volume", {
            type: "number",
            inputProps: { min: 0, step: 1000 },
            onChange: (event) =>
              onChange({
                expected_volume:
                  event.target.value === ""
                    ? null
                    : Math.max(0, Math.round(Number(event.target.value))),
              }),
          })}
        />
        <TextField
          {...field("currency", {
            select: true,
            required: false,
            fullWidth: false,
            sx: { width: 110, flexShrink: 0 },
            onChange: (event) =>
              onChange({ currency: event.target.value as Currency }),
          })}
        >
          {CURRENCIES.map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <EnumSelect
        field="deal_readiness"
        values={DEAL_READINESS}
        label={labels.dealReadiness}
        stepProps={props}
      />
    </FormGrid>
  );
};

export const ClassificationStep: React.FC<StepProps> = (props) => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const field = useField(props);

  return (
    <FormGrid>
      <EnumSelect
        field="contribution"
        values={CONTRIBUTIONS}
        label={labels.contribution}
        stepProps={props}
      />
      <Box />
      <Box sx={{ gridColumn: "1 / -1" }}>
        <TextField
          {...field("next_step", {
            multiline: true,
            minRows: 2,
            placeholder: t("wizard.nextStepPlaceholder"),
            inputProps: { maxLength: 2000 },
          })}
        />
      </Box>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <TextField
          {...field("additional_info", {
            multiline: true,
            minRows: 4,
            placeholder: t("wizard.additionalInfoPlaceholder"),
            inputProps: { maxLength: 5000 },
          })}
        />
      </Box>
    </FormGrid>
  );
};

export const DataProtectionConfirmation: React.FC<StepProps> = ({
  data,
  errors,
  onChange,
}) => {
  const { t } = useTranslation("ambassador");
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "10px",
        border: `1px solid ${errors.data_protection_confirmed ? color.red600 : color.gray200}`,
        backgroundColor: color.gray50,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={data.data_protection_confirmed}
            onChange={(event) =>
              onChange({ data_protection_confirmed: event.target.checked })
            }
          />
        }
        label={t("wizard.dataProtection")}
        sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.25 } }}
      />
      {errors.data_protection_confirmed && (
        <FormHelperText error>
          {t("wizard.errors.confirmRequired")}
        </FormHelperText>
      )}
    </Box>
  );
};
